import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { type Manifest, type Story, loadManifest, url } from "./content";

/** Lowercase without accents, so "cafe" finds "Café". */
const fold = (s: string) => s.normalize("NFD").replace(/\p{Mn}+/gu, "").toLowerCase();

export default function HomePage() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    loadManifest().then(setManifest, () => setError(true));
  }, []);

  type Shown = { story: Story; hit: string | null; at: number };
  const stories = useMemo((): Shown[] => {
    const all = manifest?.stories ?? [];
    const q = fold(query.trim());
    if (!q) return all.map((s) => ({ story: s, hit: null, at: 1 }));
    return all.flatMap((s): Shown[] => {
      if (fold(s.title).includes(q)) return [{ story: s, hit: null, at: 1 }];
      for (const [i, scene] of s.scenes.entries()) {
        if (fold(scene.label).includes(q)) return [{ story: s, hit: scene.label, at: i + 1 }];
        const word = scene.words?.find((w) => fold(w).includes(q));
        if (word) return [{ story: s, hit: `${word} · ${scene.label}`, at: i + 1 }];
      }
      return [];
    });
  }, [manifest, query]);

  // in English, one name per language ("Spanish" for both Spains, "Arabic" rather than its own name)
  const names = new Intl.DisplayNames(["en"], { type: "language" });
  const languages = manifest?.languages.filter((l) => !l.code.startsWith("en")).map((l) => names.of(l.code.split("-")[0]) ?? l.label);

  return (
    <div className="tv-home">
      <header className="tv-hero">
        <div className="tv-brand">
          <img className="tv-logo" src="/favicon.svg" alt="" />
          <span>TapVocab</span>
        </div>
        <h1>Learn words by tapping pictures.</h1>
        <p className="tv-lede">
          Illustrated stories where everything can be tapped: hear its name, said by a real voice, in the language you’re learning.
          {languages && <> {[...new Set(languages)].join(", ")} and English.</>}
        </p>
        <p className="tv-get">
          Also on iPhone, iPad, Mac and Android, in testing now.
        </p>
      </header>

      <main className="tv-library">
        <div className="tv-library-head">
          <h2>Stories</h2>
          <input
            id="search"
            type="search"
            placeholder="Find a story, scene or word"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Find a story, scene or word"
          />
        </div>

        {error && <p className="tv-message">Couldn’t load the stories. Check your connection and reload.</p>}
        {!manifest && !error && <p className="tv-message">Loading stories…</p>}
        {manifest && stories.length === 0 && <p className="tv-message">Nothing matches “{query.trim()}”.</p>}

        <ul className="tv-grid">
          {stories.map(({ story, hit, at }) => (
            <li key={story.slug}>
              <Link to={`/story/${story.slug}/${at}`} className="tv-card">
                <img src={url(story.cover ?? story.scenes[0].image)} alt="" loading="lazy" />
                <span className="tv-card-title">{story.title}</span>
                <span className="tv-card-sub">{hit ?? `${story.scenes.length} scenes`}</span>
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <footer className="tv-footer">
        <a href="/privacy">Privacy</a>
        <span>© {new Date().getFullYear()} TapVocab</span>
      </footer>
    </div>
  );
}
